<template>
  <Card class="pa-2 subheading" :style="{ height: cardHeight, minHeight: '200px' }">
    <div id="info-header"><span class="subheading">{{ $t('SMB') }}</span> {{ $t('Live Data') }}</div>
    <DataTable
      class="scb-data-card"
      :headers="computedScbHeader"
      :items="scbData"
      :style="{ height: cardHeight, minHeight: '180px' }"
      :isPaginationFalse="true"
    >
      <template v-slot:firstCol="{ text }">
        <td>
          <v-layout row wrap align-center class="first-col-width">
            <div class="text__width">
              {{ text.name }}
            </div>
            <div v-if="status.parameter">
              <v-icon
                :color="
                  text[`${status.parameter}1`] == undefined ||
                  text[`${status.parameter}1`] == null ||
                  text[`${status.parameter}1`] == 'No Data'
                    ? 'grey'
                    : executeOp(
                        status.condition.op,
                        text[`${status.parameter}1`],
                        status.condition.value
                      )
                    ? 'green'
                    : 'red'
                "
                class="icon-class"
                size="10"
              >
                circle</v-icon
              >
            </div>
          </v-layout>
        </td>
      </template>
    </DataTable>
  </Card>
</template>

<script>
const momentTz = require('moment-timezone')
import _ from "lodash";
import { mapGetters } from "vuex";
import { mapGettersMixin } from "@/util/mixin/index";
import ScbService from "@/services/Scbs";

export default {
  mixins: [mapGettersMixin],
  props: {
    parameter: {
      type: Array,
      default: function () {
        return [];
      },
    },
    parameterNameMap: {
      type: Object,
      default: function () {
        return {};
      },
    },
    nameMap: {
      type: Object,
      default: function () {
        return {};
      },
    },
    status: {
      type: Object,
      default: function () {
        return {
          parameter: "",
          limits: {},
        };
      },
    },
    cardHeight: {
      type: String,
      default: "24vh",
    },
  },
  data() {
    return {
      generalHeader: [
        {
          text: "SMB Name",
          align: "center",
          sortable: false,
          value: "name",
          width: "20%",
        },
      ],
      scbHeader: [],
      scbData: [],
    };
  },
  computed: {
    ...mapGetters(["getSelectedPlantId", "getActivePlantTimezone"]),
    computedScbHeader() {
      const cloneHeader = _.cloneDeep(this.scbHeader)
      return cloneHeader.map(el => {
        el['text'] = this.$t(el.text)
        return el
      })
    }
  },
  mounted() {
    this.parameter.forEach((p) => {
      const headerObj = {};
      headerObj["value"] = p;

      const config = Object.values(this.parameterNameMap)[0][0].config[p]
      const name = config.name
      const unit = config.unit ? config.unit : ''
      headerObj['text'] = `${name}${unit ? ` (${unit})` : ''}`
      headerObj["align"] = "center";
      this.generalHeader.push(headerObj);
    });
    this.liveScbData();
    this.mqttHandler();
  },
  beforeDestroy() {
    this.$mqtt.unsubscribe(`live/${this.getSelectedPlantId}/scb`);
  },
  methods: {
    initialStateValues() {
      this.generalHeader = [
        {
          text: "SMB Name",
          align: "center",
          sortable: false,
          value: "name",
          width: "20%",
        },
      ];
      this.scbData = [];
      this.scbHeader = [];
    },
    isFloat(value) {
      if (
        typeof value === "number" &&
        !Number.isNaN(value) &&
        !Number.isInteger(value)
      ) {
        return true;
      }

      return false;
    },
    toThreeDecimalPlaces(value) {
      if (value) {
        if (this.isFloat(Number(value))) {
          return value == 0 ? 0 : Number.parseFloat(value).toFixed(3);
        } else {
          return value;
        }
      } else {
        return "No Data";
      }
    },
    async liveScbData() {
      const idList = [...new Set(Object.keys(this.nameMap).map((el) => el.split("-").slice(0, 3).join("-")))];
      const groupedBlock = _.groupBy(idList, (id) => id.split("-").slice(0, 1));
      const dateStr = momentTz().tz(this.getActivePlantTimezone).format("YYYY-MM-DD");

      const countList = Object.values(this.parameterNameMap).map(arrD => 
        (arrD[0] && arrD[0].config && arrD[0].config.current && arrD[0].config.current.count) || 0
      );
      const maxCount = countList.length ? Math.max(...countList) : 0;
      let scbHeader = [...this.generalHeader];

      const fetchPromises = Object.keys(groupedBlock).map(blockId => {
        const param = {
          plantId: this.getSelectedPlantId,
          index: "live",
          blockId: blockId,
          modifiedId: groupedBlock[blockId],
          date: dateStr,
          parameter1: this.parameter,
          parameter2: ["current"],
        };
        return ScbService.fetchScbTableData(param)
          .then(res => res.data ? Object.values(res.data) : [])
          .catch(err => {
            console.error("ERROR LOADING LIVE SCB DATA FOR BLOCK", err);
            return [];
          });
      });

      const responses = await Promise.all(fetchPromises);
      
      let allScbData = [];
      let headerPopulated = false;

      responses.forEach(responseValue => {
        if (!responseValue || responseValue.length === 0) return;

        if (!headerPopulated && maxCount > 0) {
          const maxChannelItem = responseValue.find(elArray => elArray[0] && elArray[0].channel && elArray[0].channel.length === maxCount);
          if (maxChannelItem) {
            maxChannelItem[0].channel.forEach(ch => {
              scbHeader.push({
                text: `${ch.name} (A)`,
                value: ch.name,
                align: "center",
              });
            });
            headerPopulated = true;
          }
        }

        const processedBatch = responseValue.map(el => {
          const row = el[0];
          
          if (row.channel) {
            row.channel.forEach(ch => {
              row[ch.name] = this.toThreeDecimalPlaces(ch.current);
            });
          }

          this.generalHeader.forEach((headerObj, ind) => {
            if (ind !== 0) {
              const hVal = headerObj.value;
              let val = row[hVal];
              row[hVal] = this.toThreeDecimalPlaces(val);

              if (hVal === "status1" || hVal === "status2") {
                if (val != null && val !== undefined && val !== "No Data") {
                  row[`${hVal}1`] = val;
                  if (hVal === "status1") {
                    row[hVal] = val == 1 ? "ON" : "OFF";
                  } else if (hVal === "status2") {
                    row[hVal] = val == 1 ? "Intact" : "Blown";
                  }
                }
              }
            }
          });

          row.name = this.nameMap[row.id];
          return row;
        });

        allScbData.push(...processedBatch);
      });

      allScbData.sort((a, b) => {
        const aArr = (a.id || "").split("-");
        const bArr = (b.id || "").split("-");
        const baseA = aArr.slice(0, 3);
        const baseB = bArr.slice(0, 3);
        
        if (_.isEqual(baseA, baseB)) {
          return (+aArr[aArr.length - 1] || 0) - (+bArr[bArr.length - 1] || 0);
        }
        return (+baseA[baseA.length - 1] || 0) - (+baseB[baseB.length - 1] || 0);
      });

      this.scbHeader = scbHeader;
      this.scbData = [...this.scbData, ...allScbData];
    },
    mqttHandler() {
      this.$mqtt.subscribe(`live/${this.getSelectedPlantId}/scb`);
      if (this.mqttBound) return;
      this.mqttBound = true;
      this.$mqtt.on("message", (topic, message) => {
        if (topic.startsWith("live/")) {
          if (topic.endsWith("scb")) {
            const mqttData = JSON.parse(message.toString()).deviceList[0];
            if (mqttData.ignore_status !== 1) {
              const index = this.scbData.findIndex(
                (obj) => obj.id === mqttData.device_id
              );

              if (index === -1) return;

              this.parameter.forEach((key) => {
                this.scbData[index][key] = this.toThreeDecimalPlaces(
                  mqttData[key]
                );

                // Breaker status
                if (key == "status1") {
                  if (
                    mqttData[key] != null &&
                    mqttData[key] != undefined &&
                    mqttData[key] != 'No Data'
                  ) {
                    this.scbData[index][`${key}1`] = mqttData[key]
                    this.scbData[index][key] = mqttData[key] == 1 ? "ON" : "OFF";
                  }
                } else if (key == "status2") { // SPD Status
                  if (
                    mqttData[key] != null &&
                    mqttData[key] != undefined &&
                    mqttData[key] != 'No Data'
                  ) {
                    this.scbData[index][`${key}1`] = mqttData[key]
                    this.scbData[index][key] = mqttData[key] == 1 ? "Intact" : "Blown";
                  }
                }
              });

              if (mqttData.current) {
                const channleData = mqttData.current.split(",")
                this.scbData[index].channel.forEach((el, ind) => {
                  const chValue = this.toThreeDecimalPlaces(channleData[ind]);
                  this.scbData[index].channel[ind].value = chValue;
                  this.scbData[index][el.name] = chValue;
                });
              }
            }
          }
        }
      });
      this.$mqtt.on("error", function (error) {
        console.log("ERROR: ", error);
      });

      this.$mqtt.on("offline", function () {
        console.log("offline");
      });

      this.$mqtt.on("reconnect", function () {
        console.log("reconnect");
      });
    },
    executeOp(op, value1, value2) {
      switch (op) {
        case "eq":
          return value1 === value2;
        case "neq":
          return value1 !== value2;
        case "gt":
          return value1 > value2;
        case "gteq":
          return value1 >= value2;
        case "lt":
          return value1 < value2;
        case "lteq":
          return value1 <= value2;
        default:
          return false;
      }
    },
  },
  watch: {
    async getSelectedPlantId(newVal, oldVal) {
      this.$mqtt.unsubscribe(`live/${oldVal}/scb`);
      this.initialStateValues();
    },
    async parameterNameMap(newVal) {
      if (newVal && Object.keys(newVal).length > 0) {
        this.parameter.forEach((p) => {
          const headerObj = {};
          headerObj["value"] = p;
          const config = Object.values(newVal)[0][0]["config"][p];
          const name = config.name;
          const unit = config.unit ? config.unit : '';
          headerObj['text'] = `${name}${unit ? ` (${unit})` : ''}`;
          headerObj["align"] = "center";
          this.generalHeader.push(headerObj);
        });
        await this.liveScbData();
        this.mqttHandler();
      }
    },
  },
};
</script>

<style scoped>
.icon-class {
  margin-bottom: 2px;
  margin-left: 12px;
}

.first-col-width {
  width: 150px;
  word-wrap: break-word;
}

#info-header span {
  border-bottom: 2px solid #417cde;
  display: inline-block;
  vertical-align: top;
}

#info-header {
  padding-bottom: 16px;
}

.text__width {
  text-align: center;
  width: 120px;
}
</style>
<style>
.scb-data-card .v-table__overflow .v-table thead tr:first-child {
  position: sticky;
  top: 0px;
  z-index: 2;
  background: var(--v-primary-base);
  border-top: 1px solid #38405c;
}

.scb-data-card .v-table__overflow .v-table tbody td:first-child,
table.v-table thead th:first-child {
  position: sticky;
  left: 0;
  z-index: 1;
  background: var(--v-primary-base);
  border-right: 1px solid #38405c;
}
</style>
